import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row, Table } from 'reactstrap';
import UserLink from '../../../links/UserLink';
import { withBorder } from '../../../reactutils/withBorder';
import { withNavLocation } from '../../../reactutils/withNavLocation';
import { Highscore, TournamentRound } from '../../../services/betting';
import { MyMainGroupInternalHighscoresTop5 } from '../../BettingApp/MyMainGroupInternalHighscoresTop5';
import { LoadingIndicatorSpinner } from '../../common/LoadingIndicatorSpinner';
import { RssPortlet } from '../../common/RssPortlet';
import { Section, SectionHeading } from '../../common/Section';
import { userIdSelector } from '../../System/selectors';
import { AppFeature, hasFeature, isRadioEins } from '../../System/systemConfig';
import { RadioEinsGroupHighscores } from '../RadioEinsGroupHighscores';
import { WeeklyComment } from '../WeeklyComment';
import { portalLoadTopHighscores } from './actions';
import * as R from 'ramda';
import {
    portalLastHighscoresSelector,
    portalLastRoundSelector,
    portalLastUserHighscoreSelector,
    portalRootHighscoresSelector,
    portalRootRoundSelector,
    portalRootUserHighscoreSelector
} from './selectors';

interface TopHighscoresProps {
    round: TournamentRound;
    highscores: Highscore[];
    userHighscore?: Highscore;
}

const TopHighscores: FC<TopHighscoresProps> = props => {
    const userIsInTop5 =
        props.highscores.find(hs => props.userHighscore && hs.userId === props.userHighscore.userId) !== undefined;
    const renderHighscore = (hs: Highscore) => {
        const rankClass = props.userHighscore && hs.userId === props.userHighscore.userId ? 'bg-warning' : 'bg-white';

        return (
            <tr key={hs.id}>
                <td>
                    <span className={`p-2 ${rankClass}`}>{hs.rank}</span>
                </td>
                <td>
                    <UserLink id={String(hs.userId)} name={hs.userName} showTrophies={true} />
                </td>
            </tr>
        );
    };

    return (
        <>
            <h5>Pl. {props.round.longName}</h5>
            <Table>
                <thead>
                    <tr>
                        <th className="d-table-cell d-lg-none">Pl.</th>
                        <th className="d-none d-lg-table-cell">Platz</th>
                        <th className="d-table-cell d-lg-none">Mitsp.</th>
                        <th className="d-none d-lg-table-cell">Mitspieler</th>
                    </tr>
                </thead>
                <tbody>
                    {props.highscores.map(hs => renderHighscore(hs))}
                    {!userIsInTop5 && props.userHighscore && props.userHighscore.id !== 0 && (
                        <tr>
                            <td>
                                <span className="p-2 bg-warning text-white">{props.userHighscore.rank}</span>
                            </td>
                            <td>
                                <UserLink
                                    id={String(props.userHighscore.userId)}
                                    name={props.userHighscore.userName}
                                    showTrophies={true}
                                />
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </>
    );
};

const TopRootHighscores = () => {
    const rootRound = useSelector(portalRootRoundSelector);
    const rootHighscores = useSelector(portalRootHighscoresSelector);
    const rootUserHighscore = useSelector(portalRootUserHighscoreSelector);

    if (rootRound) {
        return <TopHighscores round={rootRound} highscores={rootHighscores} userHighscore={rootUserHighscore} />;
    } else {
        return <LoadingIndicatorSpinner />;
    }
};

const TopLastHighscores = () => {
    const lastRound = useSelector(portalLastRoundSelector);
    const lastHighscores = useSelector(portalLastHighscoresSelector);
    const lastUserHighscore = useSelector(portalLastUserHighscoreSelector);

    if (lastRound) {
        return <TopHighscores round={lastRound} highscores={lastHighscores} userHighscore={lastUserHighscore} />;
    } else {
        return <LoadingIndicatorSpinner />;
    }
};

const PortalComponent = () => {
    const rootRound = useSelector(portalRootRoundSelector);
    const userId = useSelector(userIdSelector);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(portalLoadTopHighscores());
    }, [dispatch, userId]);

    const showCommentAndRssPortal = hasFeature(AppFeature.r1comment) && hasFeature(AppFeature.rssPortal);
    return (
        <div id="r1--comment-rss-portal">
            {showCommentAndRssPortal && (
                <Section>
                    <SectionHeading>Tippen und gewinnen</SectionHeading>
                    <Container fluid={true}>
                        <Row>
                            <Col sm={3} className="bg-white">
                                <WeeklyComment />
                            </Col>
                            <Col sm={9} className="p-0">
                                <RssPortlet porletName="teaserTippspielIndexRelaunch" />
                            </Col>
                        </Row>
                    </Container>
                </Section>
            )}
            <Section>
                <SectionHeading>Tipper-Überblick</SectionHeading>
                <Container  fluid={true}>
                    <Row className="bg-light">
                        <Col className="bg-white p-2 m-2">
                            <TopRootHighscores />
                        </Col>
                        <Col className="bg-white p-2 m-2">
                            <TopLastHighscores />
                        </Col>
                        <Col className="bg-white p-2 m-2">
                            {isRadioEins() && rootRound && <RadioEinsGroupHighscores trId={String(rootRound.id)} />}
                            {!isRadioEins() && rootRound && (
                                <MyMainGroupInternalHighscoresTop5 trId={String(rootRound.id)} />
                            )}
                        </Col>
                    </Row>
                </Container>
            </Section>
        </div>
    );
};

export const Portal = R.compose(withBorder(), withNavLocation('betting'))(PortalComponent);
